import React from 'react';
import { ConfigSettingsDataTable } from '@/shared/components/config-settings';
import type { ConfigSetting } from '@/features/products/types/products.types';
import type { useConfigSettingMutations } from '../hooks/useConfigSettingMutations';

interface Props {
    data: ConfigSetting[];
    mutations: ReturnType<typeof useConfigSettingMutations>;
    /** When true the row edit/delete actions are hidden and the table is view-only.
     *  Used for soft-deleted (retired) products, whose config-setting routes 404. */
    readOnly?: boolean;
}

const GROUP_MAP: Record<string, string> = {
    financial: 'Financial',
    coverage: 'Coverage',
    payment_method: 'Payment Method', // D8 — without this the row renders "Uncategorised"
    policies: 'Policies',
    general: 'General',
};

// The engine sorts labels missing from this list last (not first, as the old indexOf(-1)
// did); still add new groups to both maps for intentional placement.
const GROUP_ORDER = ['Financial', 'Coverage', 'Payment Method', 'Policies', 'General', 'Uncategorised'];

const groupLabel = (s: ConfigSetting): string => {
    const raw = s.configOption?.group;
    return raw && GROUP_MAP[raw] ? GROUP_MAP[raw] : 'Uncategorised';
};

// Settings keyed `features` are a repeatable multi-row set addressed by id; every other
// key is unique per product and addressed by key.
const REPEATABLE_KEYS = ['features'];

const PERMISSIONS = {
    edit: { resource: 'smr', action: 'product.edit' },
    delete: { resource: 'smr', action: 'product.delete' },
} as const;

export const ConfigSettingDataTable: React.FC<Props> = ({ data, mutations, readOnly = false }) => (
    <ConfigSettingsDataTable<ConfigSetting>
        data={data}
        mutations={mutations}
        permissions={PERMISSIONS}
        entityNoun="product"
        groupLabel={groupLabel}
        groupOrder={GROUP_ORDER}
        repeatableKeys={REPEATABLE_KEYS}
        readOnly={readOnly}
    />
);
